import { CommonModule } from '@angular/common';
import { Component, inject, OnInit } from '@angular/core';
import { FormsModule } from '@angular/forms';
import { MatProgressSpinnerModule } from '@angular/material/progress-spinner';
import { MatTableModule } from '@angular/material/table';
import { ActivatedRoute } from '@angular/router';
import { OutputExcelData } from '@models/output-data.model';
import { IPredictorData } from '@models/predictor-data.model';
import { ModelType } from '@shared/constants/model-type.enum';
import { CalculationService } from 'src/app/services/calculation.service';
import { ExcelHelper } from 'src/app/helpers/excel-helper';
import { Workbook } from 'exceljs';
import { Additive, Categorical, IModelPlatformProcessor, Task } from '@models/model-platform-processor.model';
import { IModelPlatformProcessorResponse } from '@models/model-platform-processor-response.model';
import { SharedService } from 'src/app/services/shared.service';

@Component({
  selector: 'app-predictor',
  standalone: true,
  imports: [CommonModule, MatProgressSpinnerModule, MatTableModule, FormsModule],
  providers: [ExcelHelper],
  templateUrl: './predictor.component.html',
  styleUrl: './predictor.component.scss'
})
export class PredictorComponent implements OnInit {
  loading: boolean = false;
  projectId: number = 0;
  modelName: string = "";
  isNonMerit: boolean = false;
  meritValue: number = 1000;
  outputTypes: string[] = ["Statistician", "Formulation"];
  warningLabel: string = "WARNING: This project has not yet been approved for general application by the global formulators. If you are using the models in this project to predict test results, determine impact of formulation changes, or estimate probability of pass to make program plans and budget estimates or to share with customers, you should consult with the formulator and or the statistician responsible to understand applicability.";
  predictorData: IPredictorData = {
    predictorTableData: [],
    categoricalData: [],
    continuousTableData: []
  }
  selectedOutputValue: string = this.outputTypes[0];
  modelType: ModelType = ModelType.None;
  calculationResult: IModelPlatformProcessorResponse = [];
  private calculationService = inject(CalculationService);
  sharedService = inject(SharedService);

  constructor(private route: ActivatedRoute) { }

  ngOnInit(): void {
    this.loading = true;
    this.route.queryParams.subscribe(params => {
      this.projectId = params['projectId'];
      this.modelName = params['label'];
      this.modelType = params['modelType'];

      if (this.modelType) {
        this.getPredictorData();
      } else {
        this.loading = false;
      }
    });
  }

  getPredictorData(): void {
    this.calculationService.getPredictorData(this.projectId, this.modelType).subscribe({
      next: (data: IPredictorData) => {
        this.predictorData = {
          ...this.predictorData,
          ...data,
          predictorTableData: data.predictorTableData?.map((row, i) => ({
            ...this.predictorData.predictorTableData[i],
            ...row,
            prediction: row?.prediction ?? 0,
            lowerConfidence: row?.lowerConfidence ?? 0,
            upperConfidence: row?.upperConfidence ?? 0,
            probabilityOfPass: row?.probabilityOfPass ?? 0,
            lowerPassLimit: row?.lowerPassLimit,
            upperPassLimit: row?.upperPassLimit
          })) ?? []
        };
        this.isNonMerit = data.predictorTableData[0].isNonMerit;
        this.loading = false;
      }});
  }

  output(): void {
    const suffix = this.selectedOutputValue.charAt(0);
    const fileName = `${this.modelName}_${ExcelHelper.formatDateForFileName()}_${suffix}.xlsx`;

    let formulationData = this.getFormulationStatistitianRow();
    let predictorData = this.getPredictorStatistitianData();
    let modelData = this.getModelStatistitianData();

    if (suffix === 'S') {
      this.generateStatisticianOutput(fileName, formulationData, predictorData, modelData);
    } else if (suffix === 'F') {
      this.generateFormulationOutput(fileName, formulationData, predictorData, modelData);
    }
  }

  generateStatisticianOutput(fileName: string, formulationData: OutputExcelData[], predictorData: OutputExcelData[], modelData: OutputExcelData[]): void {
    let outputStatistianData = this.getOutputData(formulationData, predictorData, modelData);
    const header = outputStatistianData.map(x => [x.name]);
    const data: any[] = [outputStatistianData.map(x => [x.value])];
    let workbook = new Workbook();
    let worksheet = workbook.addWorksheet(fileName);

    ExcelHelper.addHeaderRow(worksheet, header.flat());

    data.forEach(d => {
      worksheet.addRow(d.flat());
    });

    ExcelHelper.saveWorkbook(workbook, fileName);
  }

  generateFormulationOutput(fileName: string, formulationData: OutputExcelData[], predictorData: OutputExcelData[], modelData: OutputExcelData[]): void {
    let workbook = new Workbook();
    let worksheet = workbook.addWorksheet(fileName);

    ExcelHelper.addSection(worksheet, 'Formulation', formulationData.map(x => [x.name, x.value]));
    worksheet.addRow([]);
    ExcelHelper.addSection(worksheet, 'Weighted Total Demerit', predictorData.slice(0, 6).map(x => [x.name, x.value]), false);
    worksheet.addRow([]);
    ExcelHelper.addSection(worksheet, 'Top Groove Fill', predictorData.slice(-6).map(x => [x.name, x.value]), false);
    worksheet.addRow([]);
    ExcelHelper.addSection(worksheet, 'Model Data', modelData.map(x => [x.name, x.value]));
    ExcelHelper.saveWorkbook(workbook, fileName);
  }

  getOutputData(formulationData: OutputExcelData[], predictorData: OutputExcelData[], modelData: OutputExcelData[]): OutputExcelData[] {
    let result: OutputExcelData[] = [];
    result.push(...formulationData);
    result.push(...predictorData);
    result.push(...modelData);
    return result;
  }

  getFormulationStatistitianRow(): OutputExcelData[] {
    let formulationArr: OutputExcelData[] = [];
    for (let row of this.predictorData.continuousTableData) {
      formulationArr.push({ name: row.name, value: row.value });
    }
    return formulationArr;
  }

  getPredictorStatistitianData(): OutputExcelData[] {
    let predictorResult: OutputExcelData[] = [];
    for (let row of this.predictorData.predictorTableData) {
      predictorResult.push({ name: 'Prediction', value: row.prediction });
      predictorResult.push({ name: 'Lower Confidence', value: row.lowerConfidence });
      predictorResult.push({ name: 'Upper Confidence', value: row.upperConfidence });
      predictorResult.push({ name: 'Probability of Pass', value: row.probabilityOfPass });
      predictorResult.push({ name: 'Lower Pass Limit', value: row.lowerPassLimit });
      predictorResult.push({ name: 'Upper Pass Limit', value: row.upperPassLimit });
    }

    return predictorResult;
  }

  getModelStatistitianData(): OutputExcelData[] {
    return [
      { name: 'Overall Probability of Pass', value: this.getProbabilityOfPass() },
      { name: 'Merit Pass Limit', value: '' },
      { name: 'Merit Rating', value: '' },
    ];
  }

  mapPredictorData(): Task[] {
    return this.predictorData.predictorTableData.map((item, index) => ({
      index: index + 1,
      modelID: item.modelId,
      projectID: this.projectId,
      uiName: this.modelName,
      dbName: this.modelName,
      minProbPass: item.probabilityOfPass,
      maxPredicted: item.upperPassLimit,
      minPredicted: item.lowerPassLimit,
      upperPass: item.upperPassLimit,
      lowerPass: item.lowerPassLimit,
      ignore: false
    }));
  }

  mapAdditivesData(): Additive[] {
    return this.predictorData.continuousTableData.map((item, index) => ({
      index: index + 1,
      type: 1,
      value: item.value,
      maxValue: item.upperLimit,
      minValue: item.lowerLimit,
      name: item.name
    }));
  }

  mapCategoricalData(): Categorical[] {
    return this.predictorData.categoricalData.map((item, index) => ({
      index: index + 1,
      name: item.name,
      value: item.selectedValue
    }));
  }

  getRating(): number {
    return this.calculationResult[0]?.testResult?.merit;
  }

  getProbabilityOfPass(): number {
    return this.calculationResult[0]?.testResult?.tProbPass;
  }

  isProbabilityHigh(): boolean {
    return this.getProbabilityOfPass() > 0.5;
  }

  getPredictionClass(probabilityOfPass: number): string {
    if (probabilityOfPass === undefined || probabilityOfPass === null) return '';
    return probabilityOfPass > 0.5 ? 'high-probability' : 'low-probability';
  }

  mapResultDataToGrid(): void {
    this.predictorData.predictorTableData = this.predictorData.predictorTableData.map((row, i) => ({
      ...this.predictorData.predictorTableData[i],
      ...row,
      prediction: this.calculationResult[0]?.taskResults[i]?.predictedValue,
      lowerConfidence: this.calculationResult[0]?.taskResults[i]?.lowerConfidence,
      upperConfidence: this.calculationResult[0]?.taskResults[i]?.upperConfidence,
      probabilityOfPass: this.calculationResult[0]?.taskResults[i]?.probabilityOfPass,
    }));
  }

  calculate(): void {
    const input: IModelPlatformProcessor = {
      tests: [{
        index: 1,
        projectID: this.projectId,
        projectName: this.modelName,
        uiName: this.modelName,
        dbName: this.modelName,
        meritPass: this.meritValue,
        minMerit: null,
        minTProbPass: null,
        includedTasks: this.mapPredictorData()
      }],
      additives: this.mapAdditivesData(),
      categoricals: this.mapCategoricalData()
    };
    
    this.loading = true;
    this.calculationService.calculate(input, this.modelType).subscribe({
      next: (data: IModelPlatformProcessorResponse) => {
        this.calculationResult = data;
        this.mapResultDataToGrid();
        this.loading = false;
      }});
  }
}
